import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import MarkdownRenderer from '../Markdown/MarkdownRenderer';

const WikiContent = ({ slug }) => {
  const { i18n } = useTranslation();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadContent = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Try to load markdown content from public/content directory
      const contentPath = `/content/${i18n.language}/${slug}.md`;
      const response = await fetch(contentPath);
      
      if (!response.ok) {
        setContent(getDefaultContent(slug));
        return;
      }
      
      const markdownContent = await response.text();
      
      // Check if the response is HTML (404 page) instead of markdown
      if (markdownContent.includes('<!DOCTYPE html>') || markdownContent.includes('<html')) {
        setContent(getDefaultContent(slug));
        return;
      }
      
      setContent(markdownContent);
    } catch (err) {
      console.error('Error loading content:', err);
      setError(err.message);
      // Fallback to default content
      setContent(getDefaultContent(slug));
    } finally {
      setLoading(false);
    }
  }, [slug, i18n.language]);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  const getDefaultContent = (slug) => {
    const defaultContent = {
      'getting-started': `# Getting Started

## What is Caelus Emission API

The Caelus Emission API is a powerful tool for calculating and tracking CO2 emissions across your organization. Built on the GHG Protocol methodology, it provides accurate emission calculations using standardized emission factors from trusted sources like GLEC, DEFRA, and IPCC.

## Quick Start in 5 Minutes

### Step 1: Get Your API Key
1. Log into your Caelus account
2. Navigate to Settings > API Keys
3. Click "Generate New Key"
4. Copy your API key (you'll only see it once)

### Step 2: Make Your First Request
\`\`\`bash
curl -X POST https://api.caelus.com/emissions/calculate \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "projectId": 1,
    "activityId": "road-transport-diesel",
    "region": "PL",
    "unit": "km",
    "distance": 100,
    "distanceUnit": "km"
  }'
\`\`\`

### Step 3: View Results
\`\`\`json
{
  "co2e": 25.5,
  "co2eUnit": "kg",
  "normalizedInput": 100,
  "normalizedInputUnit": "km",
  "appliedRegion": "PL",
  "scopeMajor": 1,
  "scopeCategory": "Stationary combustion"
}
\`\`\``,
      'authentication': `# Authentication

## API Key Types

### Personal Keys
- **Access**: Your projects only
- **Rate Limit**: 1,000 requests/hour
- **Use Case**: Development, testing, personal projects
- **Expiration**: Never (unless revoked)

### Project Keys
- **Access**: Specific project(s)
- **Rate Limit**: 5,000 requests/hour
- **Use Case**: Production applications
- **Expiration**: Configurable (1-365 days)

### Admin Keys
- **Access**: All projects in organization
- **Rate Limit**: 10,000 requests/hour
- **Use Case**: Enterprise integrations
- **Expiration**: Configurable (1-90 days)

## How to Get a Key

### Step 1: Access API Settings
1. Log into your Caelus account
2. Click on your profile picture
3. Select "API Settings"

### Step 2: Generate Key
1. Click "Generate New Key"
2. Enter a descriptive name
3. Select key type and permissions
4. Set expiration date (if applicable)

### Step 3: Secure Storage
- Copy the key immediately
- Store in environment variables
- Never commit to version control`,
      'faq': `# FAQ

## Frequently Asked Questions

### General Questions

#### What is the Caelus Emission API?
The Caelus Emission API is a service that calculates CO2 emissions for various activities using standardized emission factors and GHG Protocol methodology.

#### How accurate are the calculations?
Calculations are based on authoritative sources like GLEC, DEFRA, and IPCC with typical accuracy of ±5-10% depending on the activity and region.

#### What regions are supported?
We support all major countries and regions including EU, US, UK, Canada, Australia, and many others. Global factors are available as fallback.

#### How often are emission factors updated?
Major factors are updated monthly, regional factors quarterly, and comprehensive factors annually.

### Technical Questions

#### What is the rate limit?
- Personal keys: 1,000 requests/hour
- Project keys: 5,000 requests/hour
- Admin keys: 10,000 requests/hour

#### Can I cache results?
Yes, caching is recommended for repeated calculations. Cache results for 1-24 hours depending on your use case.

#### What happens if an emission factor is not found?
The API will return a 404 error with details about the missing factor. You can:
1. Use a different activity ID
2. Use a different region
3. Contact support for custom factors`,
      'emission-factors': `# Emission Factors

## Overview

Emission factors are standardized values that represent the amount of greenhouse gas emissions per unit of activity. They are essential for calculating your organization's carbon footprint accurately.

## Data Sources

Our emission factors are sourced from authoritative organizations:

- **GLEC Framework**: Global Logistics Emissions Council
- **DEFRA**: UK Department for Environment, Food & Rural Affairs
- **IPCC**: Intergovernmental Panel on Climate Change
- **EPA**: US Environmental Protection Agency
- **Ecoinvent**: Life cycle inventory database

## Factor Categories

### Energy
- Electricity grid factors by country/region
- Natural gas, coal, oil factors
- Renewable energy factors

### Transportation
- Road transport by vehicle type and fuel
- Air transport by distance and class
- Rail and water transport

### Industrial Processes
- Manufacturing processes
- Chemical reactions
- Material production

### Agriculture
- Livestock emissions
- Crop production
- Land use changes

## Usage

Emission factors are automatically applied when you make API requests. Simply specify the activity type and region, and we'll use the most appropriate factor.

\`\`\`json
{
  "activityType": "road_transport",
  "region": "PL",
  "vehicleType": "passenger_car",
  "fuelType": "diesel"
}
\`\`\`

## Updates

Emission factors are updated regularly to reflect the latest scientific data and regulatory requirements. Major updates are announced in advance.`
    };

    return defaultContent[slug] || `# ${slug}

Content for this page is not available yet. Please check back later.`;
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  // If no content, show a message
  if (!content) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Content Not Found</h1>
          <p className="text-gray-600">The requested page could not be found.</p>
          <p className="text-sm text-gray-500 mt-2">Slug: {slug}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Content Loading Error
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>Unable to load the requested content. Showing default content instead.</p>
                <p className="mt-1 text-xs">Error: {error}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-8">
          <MarkdownRenderer content={content} />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-8">
      <MarkdownRenderer content={content} />
    </div>
  );
};

export default WikiContent;
